import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { resolveEntitiesService } from '../../apis/EntitiesSelection';
import { fetchSqlTables, getConnectionById } from '../../apis/Connections';

function getConnectorName(connection = {}) {
  const raw =
    connection?.connector_name ||
    connection?.connector ||
    connection?.type ||
    '';
  return typeof raw === 'string' ? raw.toLowerCase() : '';
}

function isSqlConnectorConfig(config = {}) {
  const connector =
    config?.connection?.connector_name ||
    config?.connection?.connector ||
    config?.label ||
    config?.type ||
    '';
  return typeof connector === 'string'
    ? connector.toLowerCase().includes('sql')
    : false;
}

function buildSqlMetadataFromConnection(connection = {}) {
  if (!connection) return null;
  if (getConnectorName(connection) !== 'sql') return null;
  const params = connection.custom_params || {};
  const token =
    params.token ||
    params.quick_test_token ||
    connection.token ||
    null;
  return {
    token: token || null,
    host: params.host || '',
    port:
      params.port !== undefined && params.port !== null
        ? String(params.port)
        : '',
    database:
      params.database_name || params.db_name || params.database || '',
  };
}

async function ensureSqlMetadata(config = {}) {
  if (!config) return null;
  const direct =
    config.sqlMetadata || config.connection?.sqlMetadata || null;
  if (direct?.token) return direct;
  const connection = config.connection || {};
  const inline = buildSqlMetadataFromConnection(connection);
  if (inline?.token) return inline;
  if (connection.conn_id) {
    try {
      const fullConnection = await getConnectionById(connection.conn_id);
      const hydrated = buildSqlMetadataFromConnection(fullConnection);
      if (hydrated?.token) return hydrated;
      return hydrated || direct || null;
    } catch (err) {
      console.error('Failed to hydrate SQL metadata', err);
    }
  }
  return direct || inline || null;
}

function asArray(value) {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return Object.values(value);
  return [];
}

function normalizeSqlTablesResponse(response) {
  if (!response) return [];
  const candidates =
    response.entities ||
    response.entities?.entities ||
    response.tables ||
    response.data?.tables ||
    response.data?.entities ||
    response.data ||
    response.result?.entities ||
    response.result ||
    response;

  const normalized = asArray(candidates).map((table) => {
    if (typeof table === 'string') {
      return {
        entity_name: table,
        label: table,
        entity: table,
      };
    }
    if (!table || typeof table !== 'object') return null;
    const entityName =
      table.entity_name ||
      table.table_name ||
      table.name ||
      table.tableName ||
      '';
    if (!entityName) return null;
    const label =
      table.label ||
      table.display_name ||
      table.table_label ||
      entityName;
    return {
      ...table,
      entity_name: entityName,
      label: label || entityName,
      entity: entityName,
    };
  });

  return normalized.filter(Boolean);
}

export const fetchEntitiesIfNeeded = createAsyncThunk(
  'entities/fetchEntitiesIfNeeded',
  async (
    { workflowId, key, targetType, payload },
    { getState, rejectWithValue }
  ) => {
    const state = getState().entities;
    if (!payload.force && state.entitiesByWorkflow?.[workflowId]?.[key]) {
      return { cached: true, workflowId, key, targetType };
    }

    const shouldUseSql = isSqlConnectorConfig(payload?.config);
    const sqlMetadata = shouldUseSql
      ? await ensureSqlMetadata(payload?.config)
      : null;

    try {
      let result;
      if (shouldUseSql) {
        if (!sqlMetadata?.token) {
          return rejectWithValue({
            status: null,
            message:
              'Unable to fetch SQL tables. Please update the SQL host and port from the Connections screen.',
            needsHostPortUpdate: true,
            host: sqlMetadata?.host,
            port: sqlMetadata?.port,
            connName: payload?.config?.connection?.conn_name,
            connId: payload?.config?.connection?.conn_id,
          });
        }
        const response = await fetchSqlTables(sqlMetadata.token);
        result = normalizeSqlTablesResponse(response);
      } else {
        result = await resolveEntitiesService(payload);
      }

      return { result, workflowId, key, targetType };
    } catch (err) {
      console.log('error response of entity endpoint:', err);
      const status = err?.response?.status || err?.status || null;
      let message = null;

      if (shouldUseSql) {
        message =
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to fetch SQL tables.';
        return rejectWithValue({
          status,
          message,
          needsHostPortUpdate: true,
          host: sqlMetadata?.host,
          port: sqlMetadata?.port,
          connName: payload?.config?.connection?.conn_name,
          connId: payload?.config?.connection?.conn_id,
        });
      }

      if (err?.response?.data) {
        if (typeof err.response.data === 'string') {
          message = err.response.data;
        } else if (typeof err.response.data === 'object') {
          message = err.response.data.detail || err.response.data.message;
        }
      }
      if (!message) message = err?.message || 'Unknown error';
      return rejectWithValue({ status, message });
    }
  }
);

const initialState = {
  entitiesByWorkflow: {},
  selectedSourceEntitiesByWorkflow: {},
  selectedDestinationEntitiesByWorkflow: {},
  confirmedSourceEntitiesByWorkflow: {},
  confirmedDestinationEntitiesByWorkflow: {},
  sourceLoadingByWorkflow: {},
  destinationLoadingByWorkflow: {},
  sourceErrorByWorkflow: {},
  destinationErrorByWorkflow: {},
};

const entitiesSlice = createSlice({
  name: 'entities',
  initialState,
  reducers: {
    setSelectedSourceEntities(state, action) {
      const { workflowId, key, entities } = action.payload;
      if (!state.selectedSourceEntitiesByWorkflow)
        state.selectedSourceEntitiesByWorkflow = {};
      if (!state.selectedSourceEntitiesByWorkflow[workflowId]) {
        state.selectedSourceEntitiesByWorkflow[workflowId] = {};
      }
      state.selectedSourceEntitiesByWorkflow[workflowId][key] = entities;
    },
    setSelectedDestinationEntities(state, action) {
      const { workflowId, key, entities } = action.payload;
      if (!state.selectedDestinationEntitiesByWorkflow)
        state.selectedDestinationEntitiesByWorkflow = {};
      if (!state.selectedDestinationEntitiesByWorkflow[workflowId]) {
        state.selectedDestinationEntitiesByWorkflow[workflowId] = {};
      }
      state.selectedDestinationEntitiesByWorkflow[workflowId][key] = entities;
    },

    setConfirmedSourceEntities(state, action) {
      const { workflowId, key, entities } = action.payload;
      if (!state.confirmedSourceEntitiesByWorkflow) {
        state.confirmedSourceEntitiesByWorkflow = {};
      }

      if (!state.confirmedSourceEntitiesByWorkflow[workflowId]) {
        state.confirmedSourceEntitiesByWorkflow[workflowId] = {};
      }
      state.confirmedSourceEntitiesByWorkflow[workflowId][key] = entities;
    },
    setConfirmedDestinationEntities(state, action) {
      const { workflowId, key, entities } = action.payload;
      if (!state.confirmedDestinationEntitiesByWorkflow) {
        state.confirmedDestinationEntitiesByWorkflow = {};
      }

      if (!state.confirmedDestinationEntitiesByWorkflow[workflowId]) {
        state.confirmedDestinationEntitiesByWorkflow[workflowId] = {};
      }
      state.confirmedDestinationEntitiesByWorkflow[workflowId][key] =
        entities;
    },

    removeEntitiesForKey(state, action) {
      const { workflowId, key } = action.payload;
      if (state.entitiesByWorkflow[workflowId]) {
        delete state.entitiesByWorkflow[workflowId][key];
      }
      if (state.selectedSourceEntitiesByWorkflow[workflowId]) {
        delete state.selectedSourceEntitiesByWorkflow[workflowId][key];
      }
      if (state.selectedDestinationEntitiesByWorkflow[workflowId]) {
        delete state.selectedDestinationEntitiesByWorkflow[workflowId][key];
      }
      if (state.confirmedSourceEntitiesByWorkflow[workflowId]) {
        delete state.confirmedSourceEntitiesByWorkflow[workflowId][key];
      }
      if (state.confirmedDestinationEntitiesByWorkflow[workflowId]) {
        delete state.confirmedDestinationEntitiesByWorkflow[workflowId][key];
      }
    },
    removeAllEntitiesForWorkflow(state, action) {
      const workflowId = action.payload;
      if (
        state.entitiesByWorkflow[workflowId] ||
        state.selectedSourceEntitiesByWorkflow[workflowId] ||
        state.selectedDestinationEntitiesByWorkflow[workflowId] ||
        state.confirmedSourceEntitiesByWorkflow[workflowId] ||
        state.confirmedDestinationEntitiesByWorkflow[workflowId]
      ) {
        delete state.entitiesByWorkflow[workflowId];
        delete state.selectedSourceEntitiesByWorkflow[workflowId];
        delete state.selectedDestinationEntitiesByWorkflow[workflowId];
        delete state.confirmedSourceEntitiesByWorkflow[workflowId];
        delete state.confirmedDestinationEntitiesByWorkflow[workflowId];
      }
    },
    resetEntities(state) {
      state.entitiesByWorkflow = {};
      state.selectedSourceEntitiesByWorkflow = {};
      state.selectedDestinationEntitiesByWorkflow = {};
      state.confirmedSourceEntitiesByWorkflow = {};
      state.confirmedDestinationEntitiesByWorkflow = {};
      state.sourceLoadingByWorkflow = {};
      state.destinationLoadingByWorkflow = {};
      state.sourceErrorByWorkflow = {};
      state.destinationErrorByWorkflow = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEntitiesIfNeeded.pending, (state, action) => {
        const { workflowId, key, targetType } = action.meta.arg;
        if (targetType === 'source') {
          if (!state.sourceLoadingByWorkflow) {
            state.sourceLoadingByWorkflow = {};
          }
          if (!state.sourceLoadingByWorkflow[workflowId]) {
            state.sourceLoadingByWorkflow[workflowId] = {};
          }
          state.sourceLoadingByWorkflow[workflowId][key] = true;

          if (!state.sourceErrorByWorkflow) {
            state.sourceErrorByWorkflow = {};
          }
          if (!state.sourceErrorByWorkflow[workflowId]) {
            state.sourceErrorByWorkflow[workflowId] = {};
          }
          state.sourceErrorByWorkflow[workflowId][key] = null;

        } else if (targetType === 'destination') {
          if (!state.destinationLoadingByWorkflow) {
            state.destinationLoadingByWorkflow = {};
          }

          if (!state.destinationLoadingByWorkflow[workflowId]) {
            state.destinationLoadingByWorkflow[workflowId] = {};
          }

          state.destinationLoadingByWorkflow[workflowId][key] = true;

          if (!state.destinationErrorByWorkflow) {
            state.destinationErrorByWorkflow = {};
          }
          if (!state.destinationErrorByWorkflow[workflowId]) {
            state.destinationErrorByWorkflow[workflowId] = {};
          }
          state.destinationErrorByWorkflow[workflowId][key] = null;

        }
      })
      .addCase(fetchEntitiesIfNeeded.fulfilled, (state, action) => {
        const { workflowId, key, cached } = action.payload;

        if (cached) {
          if (action.payload.targetType === 'source') {
            if (!state.sourceLoadingByWorkflow) {
              state.sourceLoadingByWorkflow = {};
            }

            if (!state.sourceLoadingByWorkflow[workflowId]) {
              state.sourceLoadingByWorkflow[workflowId] = {};
            }
            state.sourceLoadingByWorkflow[workflowId][key] = false;

            // const { workflowName, key } = action.payload;
            if (workflowId && key) {
              if (!state.sourceErrorByWorkflow) {
                state.sourceErrorByWorkflow = {};
              }
              if (!state.sourceErrorByWorkflow[workflowId]) {
                state.sourceErrorByWorkflow[workflowId] = {};
              }
              state.sourceErrorByWorkflow[workflowId][key] = null;
            }
          } else if (action.payload.targetType === 'destination') {
            if (!state.destinationLoadingByWorkflow) {
              state.destinationLoadingByWorkflow = {};
            }

            if (!state.destinationLoadingByWorkflow[workflowId]) {
              state.destinationLoadingByWorkflow[workflowId] = {};
            }

            state.destinationLoadingByWorkflow[workflowId][key] = false;

            // const { workflowName, key } = action.payload;
            if (workflowId && key) {
              if (!state.destinationErrorByWorkflow) {
                state.destinationErrorByWorkflow = {};
              }
              if (!state.destinationErrorByWorkflow[workflowId]) {
                state.destinationErrorByWorkflow[workflowId] = {};
              }
              state.destinationErrorByWorkflow[workflowId][key] = null;
            }
          }
          return;
        } else {
          const { workflowId, key, result, targetType } = action.payload;

          if (!workflowId || typeof workflowId !== 'string') {
            console.error('Invalid or missing workflowName in entitiesSlice:', {
              workflowId,
              key,
            });
            if (targetType === 'source') {
              if (!state.sourceLoadingByWorkflow) {
                state.sourceLoadingByWorkflow = {};
              }

              if (!state.sourceLoadingByWorkflow[workflowId]) {
                state.sourceLoadingByWorkflow[workflowId] = {};
              }
              state.sourceLoadingByWorkflow[workflowId][key] = false;

              if (workflowId && key) {
                if (!state.sourceErrorByWorkflow) {
                  state.sourceErrorByWorkflow = {};
                }
                if (!state.sourceErrorByWorkflow[workflowId]) {
                  state.sourceErrorByWorkflow[workflowId] = {};
                }
                state.sourceErrorByWorkflow[workflowId][key] = 'Something went wrong. Please try again';
              }

            } else if (targetType === 'destination') {
              if (!state.destinationLoadingByWorkflow) {
                state.destinationLoadingByWorkflow = {};
              }

              if (!state.destinationLoadingByWorkflow[workflowId]) {
                state.destinationLoadingByWorkflow[workflowId] = {};
              }
              state.destinationLoadingByWorkflow[workflowId][key] = false;

              if (workflowId && key) {
                if (!state.destinationErrorByWorkflow) {
                  state.destinationErrorByWorkflow = {};
                }
                if (!state.destinationErrorByWorkflow[workflowId]) {
                  state.destinationErrorByWorkflow[workflowId] = {};
                }
                state.destinationErrorByWorkflow[workflowId][key] = 'Something went wrong. Please try again';
              }
            }
            return;
          }

          if (!state.entitiesByWorkflow) {
            state.entitiesByWorkflow = {};
          }

          if (!state.entitiesByWorkflow[workflowId]) {
            state.entitiesByWorkflow[workflowId] = {};
          }
          state.entitiesByWorkflow[workflowId][key] = result;
          if (targetType === 'source') {
            if (!state.sourceLoadingByWorkflow) {
              state.sourceLoadingByWorkflow = {};
            }

            if (!state.sourceLoadingByWorkflow[workflowId]) {
              state.sourceLoadingByWorkflow[workflowId] = {};
            }
            state.sourceLoadingByWorkflow[workflowId][key] = false;

            if (!state.sourceErrorByWorkflow) {
              state.sourceErrorByWorkflow = {};
            }

            if (!state.sourceErrorByWorkflow[workflowId]) {
              state.sourceErrorByWorkflow[workflowId] = {};
            }
            state.sourceErrorByWorkflow[workflowId][key] = null;
          } else if (targetType === 'destination') {
            if (!state.destinationLoadingByWorkflow) {
              state.destinationLoadingByWorkflow = {};
            }

            if (!state.destinationLoadingByWorkflow[workflowId]) {
              state.destinationLoadingByWorkflow[workflowId] = {};
            }
            state.destinationLoadingByWorkflow[workflowId][key] = false;

            if (!state.destinationErrorByWorkflow) {
              state.destinationErrorByWorkflow = {};
            }

            if (!state.destinationErrorByWorkflow[workflowId]) {
              state.destinationErrorByWorkflow[workflowId] = {};
            }
            state.destinationErrorByWorkflow[workflowId][key] = null;
          }
        }
      })
      .addCase(fetchEntitiesIfNeeded.rejected, (state, action) => {
        const { workflowId, key, targetType } = action.meta.arg;
        const errorMsg = action.payload || action.error?.message || 'Unknown error';


        if (targetType === 'source') {
          if (!state.sourceLoadingByWorkflow) {
            state.sourceLoadingByWorkflow = {};
          }

          if (!state.sourceLoadingByWorkflow[workflowId]) {
            state.sourceLoadingByWorkflow[workflowId] = {};
          }
          state.sourceLoadingByWorkflow[workflowId][key] = false;

          if (workflowId && key) {
            if (!state.sourceErrorByWorkflow) {
              state.sourceErrorByWorkflow = {};
            }
            if (!state.sourceErrorByWorkflow[workflowId]) {
              state.sourceErrorByWorkflow[workflowId] = {};
            }
            state.sourceErrorByWorkflow[workflowId][key] = errorMsg;
          }

        } else if (targetType === 'destination') {
          if (!state.destinationLoadingByWorkflow) {
            state.destinationLoadingByWorkflow = {};
          }

          if (!state.destinationLoadingByWorkflow[workflowId]) {
            state.destinationLoadingByWorkflow[workflowId] = {};
          }
          state.destinationLoadingByWorkflow[workflowId][key] = false;

          if (workflowId && key) {
            if (!state.destinationErrorByWorkflow) {
              state.destinationErrorByWorkflow = {};
            }
            if (!state.destinationErrorByWorkflow[workflowId]) {
              state.destinationErrorByWorkflow[workflowId] = {};
            }
            state.destinationErrorByWorkflow[workflowId][key] = errorMsg;
          }

        }
      });
  },
});

export const {
  setSelectedSourceEntities,
  setSelectedDestinationEntities,
  setConfirmedSourceEntities,
  setConfirmedDestinationEntities,
  removeEntitiesForKey,
  resetEntities,
  removeAllEntitiesForWorkflow
} = entitiesSlice.actions;

export default entitiesSlice.reducer;
